import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  LinearProgress,
  Skeleton,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  TextField,
  Chip,
} from "@mui/material";
import { API_URL } from "../../common/AppConstants";
import SignaturePad from "signature_pad";
import { useSnackbar } from "notistack";
import { generateSignedWaiverWithData } from "../../common/PdfTemplates";
import { useStoreDetails } from '../../common/Providers/StoreDetailsProvider/UseStoreDetails';

type DocEntry = {
  id: number;
  type: string;
};

type DocRequest = {
  docs: DocEntry[];
  owner_name: string;
  driver_type: string;
  identifier?: string;
  email?: string;
  phone?: string;
};

// key: `${owner_name}__${doc_type}` e.g. "adil asad__DRIVER_LICENSE"
type PerPersonFiles = Record<string, File | null>;
type PerPersonUploaded = Record<string, boolean>;
// key: `${owner_name}__BAC_TEST` — BAC score string
type PerPersonBacScores = Record<string, string>;

const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_VIDEO_MB = 200;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

const docKey = (ownerName: string, docType: string) => `${ownerName}__${docType}`;

const RentalAgreement: React.FC = () => {
  const [isVerifying, setIsVerifying] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { storeDetails } = useStoreDetails();

  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [reservation, setReservation] = useState<any | null>(null);

  // per-person selected files: key = `${owner_name}__${doc_type}`
  const [personFiles, setPersonFiles] = useState<PerPersonFiles>({});
  // per-person uploaded flags (set after successful upload)
  const [personUploaded, setPersonUploaded] = useState<PerPersonUploaded>({});
  // per-person BAC scores: key = `${owner_name}__BAC_TEST`
  const [personBacScores, setPersonBacScores] = useState<PerPersonBacScores>({});

  const [signaturePadDirty, setSignaturePadDirty] = useState<boolean>(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [hasSignedAgreement, setHasSignedAgreement] = useState<boolean>(false);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState<boolean>(false);
  const [waiverPdfUrl, setWaiverPdfUrl] = useState<string>("/templates/waiver_template_with_fields.pdf");
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const docRequests: DocRequest[] = reservation?.doc_requests ?? [];

  const needsLicense = useMemo(() => docRequests.some(r => r.docs.some(d => d.type === "DRIVER_LICENSE")), [reservation]);
  const needsInsurance = useMemo(() => docRequests.some(r => r.docs.some(d => d.type === "INSURANCE")), [reservation]);
  const needsWaiver = useMemo(() => docRequests.some(r => r.docs.some(d => d.type === "WAIVER")), [reservation]);
  const needsBAC = useMemo(() => docRequests.some(r => r.docs.some(d => d.type === "BAC_TEST")), [reservation]);

  const activeSteps = useMemo(() => {
    if (!storeDetails) return [];
    const steps: { key: string }[] = [];
    if (needsLicense) steps.push({ key: "license" });
    if (needsInsurance) steps.push({ key: "insurance" });
    if (needsBAC) steps.push({ key: "bac" });
    // The reservation's doc_requests are authoritative for what THIS reservation
    // requires. Do not gate the waiver behind the store-wide is_agreement_required
    // flag (which defaults to false), otherwise a required waiver gets silently
    // dropped and bypassed when the flag is false/missing/not-yet-loaded.
    if (needsWaiver) steps.push({ key: "agreement" });
    return steps;
  }, [storeDetails, needsLicense, needsInsurance, needsBAC, needsWaiver]);

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const currentStep = activeSteps[currentStepIndex]?.key;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === activeSteps.length - 1;

  // verify token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setError("Invalid or missing token");
      setIsVerifying(false);
      return;
    }
    setToken(t);

    const verifyReservation = async () => {
      try {
        setIsVerifying(true);
        const response = await fetch(
          `${API_URL}/reservations/verifypubliclinkandgetreservation`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: t }),
          }
        );

        if (!response.ok) {
          const { error } = await response.json();
          throw new Error(error || "Failed to verify link");
        }

        const result = await response.json();
        const resData = result.data || {};
        setReservation(resData);
      } catch (err: any) {
        const rawMsg: string = err.message || "Something went wrong";
        const friendlyMsg = rawMsg.toLowerCase().includes("invalid") || rawMsg.toLowerCase().includes("expired") || rawMsg.toLowerCase().includes("token")
          ? "This link has expired. Please contact us to receive a new link."
          : rawMsg;
        setError(friendlyMsg);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyReservation();
  }, []);

  // initialize signature pad after canvas mounts
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(ratio, ratio);
    };

    resizeCanvas();

    const pad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255,255,255,0)",
      penColor: "rgb(20,20,20)",
    });
    sigPadRef.current = pad;
    setSignaturePadDirty(false);
    pad.addEventListener("afterUpdateStroke", () => setSignaturePadDirty(true));

    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      pad.off();
      sigPadRef.current = null;
    };
  }, [currentStep]);

  // cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  /* ---------- helpers: per-person file selection ---------- */
  const setPersonFile = (ownerName: string, docType: string, file: File | null) => {
    const key = docKey(ownerName, docType);
    if (!file) {
      setPreviewUrls(prev => {
        if (prev[key]) {
          URL.revokeObjectURL(prev[key]);
          const { [key]: removed, ...rest } = prev;
          return rest;
        }
        return prev;
      });
      setPersonFiles(prev => ({ ...prev, [key]: null }));
      return;
    }
    const isVideo = file.type.startsWith("video/");
    const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_FILE_BYTES;
    const maxMb = isVideo ? MAX_VIDEO_MB : MAX_FILE_MB;
    if (file.size > maxBytes) {
      enqueueSnackbar(`File too large. Max ${maxMb} MB.`, { variant: "error" });
      return;
    }
    setPreviewUrls(prev => {
      if (prev[key]) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: URL.createObjectURL(file) };
    });
    setPersonFiles(prev => ({ ...prev, [key]: file }));
  };

  /* ---------- helper: BAC score input ---------- */
  const setBacScore = (ownerName: string, value: string) => {
    const key = docKey(ownerName, "BAC_TEST");
    setPersonBacScores(prev => ({ ...prev, [key]: value }));
  };

  const getBacScore = (ownerName: string): string => {
    return personBacScores[docKey(ownerName, "BAC_TEST")] ?? "";
  };

  const bacLimit = parseFloat(storeDetails?.bac_limit ?? "0.10") || 0.10;

  const isBacScoreValid = (score: string): boolean => {
    if (score === "") return false;
    const n = parseFloat(score);
    return !isNaN(n) && n >= 0 && n <= bacLimit;
  };

  /* ---------- build document metadata ---------- */
  const buildMetadata = (req: DocRequest, documentType: string, sortOrder: number, documentId?: number) => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 11);
    const identifier = req.identifier ?? req.owner_name.toLowerCase().replace(/\s+/g, "_");
    const uniqueId = `${documentType.toLowerCase()}_${identifier}_${timestamp}_${random}`;
    return {
      id: uniqueId,
      document_id: documentId ?? null,
      sort_order: sortOrder,
      owner: { type: req.driver_type || "DRIVER", name: req.owner_name, identifier, email: req.email ?? null, phone: req.phone ?? null },
    };
  };

  /* ---------- upload BAC_TEST document with score ---------- */
  const uploadBACDocument = async (
    entry: { file: File; req: DocRequest; sortOrder: number; documentId: number; bacScore: string }
  ) => {
    if (!token || !reservation) throw new Error("Missing token or reservation");

    const form = new FormData();
    form.append("file", entry.file);
    form.append("token", token);
    form.append("document_id", String(entry.documentId));
    form.append("customer_id", reservation?.customer?.id ?? "");
    form.append("reservation_id", reservation?.reservation_id ?? "");

    const resp = await fetch(`${API_URL}/reservations/uploadbactestvideo/`, {
      method: "POST",
      body: form,
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ message: resp.statusText }));
      throw new Error(err.message || "BAC upload failed");
    }
    return resp;
  };

  /* ---------- upload multiple documents (same type) in one request ---------- */
  const uploadMultipleDocuments = async (
    entries: { file: File; req: DocRequest; sortOrder: number; documentId: number }[],
    documentType: "DRIVER_LICENSE" | "INSURANCE"
  ) => {
    if (!token || !reservation) {
      throw new Error("Missing token or reservation");
    }

    const form = new FormData();
    const metadataArray = entries.map(({ req, sortOrder, documentId }) =>
      buildMetadata(req, documentType, sortOrder, documentId)
    );

    entries.forEach(({ file }) => form.append("files", file));
    form.append("metadata", JSON.stringify(metadataArray));
    form.append("token", token);
    form.append("document_type", documentType);
    form.append("customer_id", reservation?.customer?.id ?? "");
    form.append("reservation_id", reservation?.reservation_id ?? "");

    const resp = await fetch(`${API_URL}/reservations/uploaddocuments/`, {
      method: "POST",
      body: form,
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ message: resp.statusText }));
      throw new Error(err.message || "Upload failed");
    }

    return resp;
  };

  /* ---------- upload waiver/signature as WAIVER doc ---------- */
  const uploadSignatureAsWaiver = async (blob: Blob) => {
    if (!token || !reservation) {
      throw new Error("Missing token or reservation");
    }

    const pdfBlob =
      blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });

    const filename = `signature_${reservation?.reservation_id ?? "doc"}.pdf`;
    const file = new File([pdfBlob], filename, { type: "application/pdf" });

    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 11);
    const waiverDocEntry = docRequests.flatMap(r => r.docs).find(d => d.type === "WAIVER");
    const waiverDocId = waiverDocEntry?.id;
    const metadata = [{
      id: `waiver_primary_${timestamp}_${random}`,
      document_id: waiverDocId ?? null,
      sort_order: 0,
      owner: {
        type: "DRIVER",
        name: reservation?.customer?.name ?? "Customer",
        identifier: "primary",
      },
    }];

    const form = new FormData();
    form.append("files", file);
    form.append("metadata", JSON.stringify(metadata));
    form.append("token", token);
    form.append("document_type", "WAIVER");
    form.append("customer_id", reservation?.customer?.id ?? "");
    form.append("reservation_id", reservation?.reservation_id ?? "");

    const resp = await fetch(`${API_URL}/reservations/uploaddocuments/`, {
      method: "POST",
      body: form,
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ message: resp.statusText }));
      throw new Error(err.message || "Upload failed");
    }

    return resp;
  };

  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---------- UI actions ---------- */
  const handlePrev = () => setCurrentStepIndex((prevIndex) => Math.max(0, prevIndex - 1));
  const handleNext = () => {
    if (currentStep === "license") {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === "DRIVER_LICENSE"))) {
        const key = docKey(req.owner_name, "DRIVER_LICENSE");
        if (!isAlreadyUploaded(req.owner_name, "DRIVER_LICENSE") && !personFiles[key]) {
          enqueueSnackbar(`Please choose a Driver's License file for ${req.owner_name} before continuing.`, { variant: "warning" });
          return;
        }
      }
    }
    if (currentStep === "insurance") {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === "INSURANCE"))) {
        const key = docKey(req.owner_name, "INSURANCE");
        if (!isAlreadyUploaded(req.owner_name, "INSURANCE") && !personFiles[key]) {
          enqueueSnackbar(`Please choose an Insurance file for ${req.owner_name} before continuing.`, { variant: "warning" });
          return;
        }
      }
    }
    if (currentStep === "bac") {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === "BAC_TEST"))) {
        const key = docKey(req.owner_name, "BAC_TEST");
        if (!isAlreadyUploaded(req.owner_name, "BAC_TEST") && !personFiles[key]) {
          enqueueSnackbar(`Please choose a BAC test video for ${req.owner_name} before continuing.`, { variant: "warning" });
          return;
        }
      }
    }
    setCurrentStepIndex((prevIndex) => Math.min(activeSteps.length - 1, prevIndex + 1));
  };

  const handleClearSignature = () => {
    sigPadRef.current?.clear();
    setSignatureDataUrl(null);
    setSignaturePadDirty(false);
  };

  const handleUndoSignature = () => {
    const pad = sigPadRef.current;
    if (!pad) return;
    const data = pad.toData();
    if (data.length === 0) return;
    data.pop();
    pad.fromData(data);
    setSignaturePadDirty(data.length > 0);
  };

  const handleAttachSignature = () => {
    const pad = sigPadRef.current;
    if (!pad) return;
    if (pad.isEmpty()) {
      enqueueSnackbar("Please sign first.", { variant: "warning" });
      return;
    }
    const dataUrl = pad.toDataURL("image/png");
    setSignatureDataUrl(dataUrl);
    enqueueSnackbar("Signature attached.", { variant: "success" });
  };

  const handlePreview = useCallback(async () => {
    const pad = sigPadRef.current;
    if (!pad || pad.isEmpty()) {
      enqueueSnackbar("Please provide a signature to preview", { variant: "warning" });
      return;
    }
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      try {
        const signature = pad.toDataURL("image/png");
        const waiverReq = docRequests.find(r => r.docs.some(d => d.type === 'WAIVER'));
        const renterName = waiverReq?.owner_name || reservation?.customer?.name || "";
        const now = new Date();
        const signedAt = now.toISOString().slice(0, 16).replace('T', ' ');
        const pdfBlob = await generateSignedWaiverWithData(signature, {
          renterName,
          renterPhone: waiverReq?.phone || "",
          renterEmail: waiverReq?.email || "",
          signedByText: `Signed by ${renterName} on ${signedAt} GMT.`,
        });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setWaiverPdfUrl(pdfUrl);
      } catch (err: any) {
        enqueueSnackbar("Failed to generate preview: " + err.message, { variant: "error" });
      }
    }, 400);
  }, [enqueueSnackbar, docRequests, reservation]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isAlreadyUploaded = (ownerName: string, docType: string): boolean =>
    !!personUploaded[docKey(ownerName, docType)];

  /* Submit All */
  const handleSubmitAll = async () => {
    if (activeSteps.some(s => s.key === 'license')) {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === 'DRIVER_LICENSE'))) {
        const key = docKey(req.owner_name, 'DRIVER_LICENSE');
        if (!isAlreadyUploaded(req.owner_name, 'DRIVER_LICENSE') && !personUploaded[key] && !personFiles[key]) {
          enqueueSnackbar(`Please upload Driver's License for ${req.owner_name}.`, { variant: 'error' });
          setCurrentStepIndex(activeSteps.findIndex(s => s.key === 'license'));
          return;
        }
      }
    }
    if (activeSteps.some(s => s.key === 'insurance')) {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === 'INSURANCE'))) {
        const key = docKey(req.owner_name, 'INSURANCE');
        if (!isAlreadyUploaded(req.owner_name, 'INSURANCE') && !personUploaded[key] && !personFiles[key]) {
          enqueueSnackbar(`Please upload Insurance for ${req.owner_name}.`, { variant: 'error' });
          setCurrentStepIndex(activeSteps.findIndex(s => s.key === 'insurance'));
          return;
        }
      }
    }
    if (activeSteps.some(s => s.key === 'bac')) {
      for (const req of docRequests.filter(r => r.docs.some(d => d.type === 'BAC_TEST'))) {
        const key = docKey(req.owner_name, 'BAC_TEST');
        if (!isAlreadyUploaded(req.owner_name, 'BAC_TEST') && !personUploaded[key] && !personFiles[key]) {
          enqueueSnackbar(`Please upload BAC test video for ${req.owner_name}.`, { variant: 'error' });
          setCurrentStepIndex(activeSteps.findIndex(s => s.key === 'bac'));
          return;
        }
      }
    }
    if (activeSteps.some(s => s.key === 'agreement') && !hasSignedAgreement) {
      const pad = sigPadRef.current;
      if (!signatureDataUrl && (!pad || pad.isEmpty())) {
        enqueueSnackbar('Please sign the waiver before submitting.', { variant: 'error' });
        setCurrentStepIndex(activeSteps.findIndex(s => s.key === 'agreement'));
        return;
      }
      if (!signatureDataUrl && pad && !pad.isEmpty()) {
        const dataUrl = pad.toDataURL("image/png");
        setSignatureDataUrl(dataUrl);
      }
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const licenseEntries: { file: File; req: DocRequest; sortOrder: number; documentId: number }[] = [];
      docRequests.filter(r => r.docs.some(d => d.type === 'DRIVER_LICENSE')).forEach((req, idx) => {
        const docEntry = req.docs.find(d => d.type === 'DRIVER_LICENSE')!;
        const key = docKey(req.owner_name, 'DRIVER_LICENSE');
        const file = personFiles[key];
        if (file && !personUploaded[key] && !isAlreadyUploaded(req.owner_name, 'DRIVER_LICENSE')) {
          licenseEntries.push({ file, req, sortOrder: idx, documentId: docEntry.id });
        }
      });
      if (licenseEntries.length > 0) {
        await uploadMultipleDocuments(licenseEntries, 'DRIVER_LICENSE');
        const updates: PerPersonUploaded = {};
        licenseEntries.forEach(({ req }) => { updates[docKey(req.owner_name, 'DRIVER_LICENSE')] = true; });
        setPersonUploaded(prev => ({ ...prev, ...updates }));
      }

      const insuranceEntries: { file: File; req: DocRequest; sortOrder: number; documentId: number }[] = [];
      docRequests.filter(r => r.docs.some(d => d.type === 'INSURANCE')).forEach((req, idx) => {
        const docEntry = req.docs.find(d => d.type === 'INSURANCE')!;
        const key = docKey(req.owner_name, 'INSURANCE');
        const file = personFiles[key];
        if (file && !personUploaded[key] && !isAlreadyUploaded(req.owner_name, 'INSURANCE')) {
          insuranceEntries.push({ file, req, sortOrder: idx, documentId: docEntry.id });
        }
      });
      if (insuranceEntries.length > 0) {
        await uploadMultipleDocuments(insuranceEntries, 'INSURANCE');
        const updates: PerPersonUploaded = {};
        insuranceEntries.forEach(({ req }) => { updates[docKey(req.owner_name, 'INSURANCE')] = true; });
        setPersonUploaded(prev => ({ ...prev, ...updates }));
      }

      const bacRequests = docRequests.filter(r => r.docs.some(d => d.type === 'BAC_TEST'));
      for (let bacIdx = 0; bacIdx < bacRequests.length; bacIdx++) {
        const req = bacRequests[bacIdx];
        const docEntry = req.docs.find((d: DocEntry) => d.type === 'BAC_TEST')!;
        const key = docKey(req.owner_name, 'BAC_TEST');
        const file = personFiles[key];
        const score = getBacScore(req.owner_name);
        if (file && !personUploaded[key] && !isAlreadyUploaded(req.owner_name, 'BAC_TEST')) {
          await uploadBACDocument({ file, req, sortOrder: bacIdx, documentId: docEntry.id, bacScore: score });
          setPersonUploaded(prev => ({ ...prev, [key]: true }));
        }
      }

      const finalSigUrl = signatureDataUrl || (sigPadRef.current && !sigPadRef.current.isEmpty() ? sigPadRef.current.toDataURL("image/png") : null);
      if (activeSteps.some(s => s.key === 'agreement') && finalSigUrl && !hasSignedAgreement) {
        const waiverReq = docRequests.find(r => r.docs.some(d => d.type === 'WAIVER'));
        const renterName = waiverReq?.owner_name || reservation?.customer?.name || "";
        const now = new Date();
        const signedAt = now.toISOString().slice(0, 16).replace('T', ' ');
        const signedPdf = await generateSignedWaiverWithData(finalSigUrl, {
          renterName,
          renterPhone: waiverReq?.phone || "",
          renterEmail: waiverReq?.email || "",
          signedByText: `Signed by ${renterName} on ${signedAt} GMT.`,
        });
        await uploadSignatureAsWaiver(signedPdf);
        setHasSignedAgreement(true);
      }
      await fetch(`${API_URL}/reservations/expirepubliclink`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, customer_id: reservation?.customer?.id }),
      }).catch(() => {});

      enqueueSnackbar('All documents submitted successfully!', { variant: 'success' });
      setSubmittedSuccessfully(true);
      // try {
      //   const resp = await fetch(
      //     `${API_URL}/reservations/verifypubliclinkandgetreservation`,
      //     { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) }
      //   );
      //   if (resp.ok) setReservation((await resp.json()).data || {});
      // } catch (_) {}
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Submission failed', { variant: 'error' });
      setError(err.message || 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------- per-person BAC card ---------- */
  const renderBACCard = (req: DocRequest) => {
    const key = docKey(req.owner_name, "BAC_TEST");
    const alreadyUploaded = isAlreadyUploaded(req.owner_name, "BAC_TEST") || !!personUploaded[key];
    const file = personFiles[key] ?? null;
    const score = getBacScore(req.owner_name);
    const scoreValid = isBacScoreValid(score);
    const inputId = `file-${req.owner_name.replace(/\s+/g, "_")}-BAC_TEST`;

    return (
      <Paper key={key} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{req.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">{req.driver_type} Driver — Breathalyzer Test</Typography>
          </Box>
          {alreadyUploaded && (
            <Alert severity="success" sx={{ py: 0, px: 1 }}>Uploaded</Alert>
          )}
        </Box>

        {alreadyUploaded ? (
          <Typography variant="body2" color="text.secondary">
            BAC test already submitted for {req.owner_name}.
          </Typography>
        ) : (
          <>
            {/* Video upload */}
            <input
              id={inputId}
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              onChange={(e) => {
                setPersonFile(req.owner_name, "BAC_TEST", e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <label htmlFor={inputId}>
                <Button variant="outlined" component="span" size="small">
                  🎥 {file ? "Change" : "Choose"} Video
                </Button>
              </label>
              {file && (
                <Tooltip title="Remove file">
                  <IconButton size="small" color="error" onClick={() => setPersonFile(req.owner_name, "BAC_TEST", null)}>✕</IconButton>
                </Tooltip>
              )}
            </Stack>
            {file && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <video
                  src={previewUrls[key] || ""}
                  controls
                  style={{ width: "100%", maxHeight: 200, borderRadius: 4, border: "1px solid #e6eef8" }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{file.name} — {formatBytes(file.size)}</Typography>
              </Box>
            )}
            {!file && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No video chosen</Typography>
            )}

          </>
        )}
      </Paper>
    );
  };

  /* ---------- per-person upload card ---------- */
  const renderPersonDocCard = (req: DocRequest, docType: "DRIVER_LICENSE" | "INSURANCE") => {
    const key = docKey(req.owner_name, docType);
    const alreadyUploaded = isAlreadyUploaded(req.owner_name, docType) || personUploaded[key];
    const file = personFiles[key] ?? null;
    const previewUrl = previewUrls[key] ?? null;
    const label = docType === "DRIVER_LICENSE" ? "Driver's License" : "Insurance";
    const inputId = `file-${req.owner_name.replace(/\s+/g, "_")}-${docType}`;

    return (
      <Paper key={key} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{req.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">{req.driver_type} Driver — {label}</Typography>
          </Box>
          {alreadyUploaded && (
            <Alert severity="success" sx={{ py: 0, px: 1 }}>Uploaded</Alert>
          )}
        </Box>

        {alreadyUploaded ? (
          <Typography variant="body2" color="text.secondary">
            {label} already submitted for {req.owner_name}.
          </Typography>
        ) : (
          <>
            <input
              id={inputId}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              onChange={(e) => {
                setPersonFile(req.owner_name, docType, e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <label htmlFor={inputId}>
                <Button variant="outlined" component="span" size="small">
                  📁 {file ? "Change" : "Choose"} {label}
                </Button>
              </label>
              {file && (
                <Tooltip title="Remove file">
                  <IconButton size="small" color="error" onClick={() => setPersonFile(req.owner_name, docType, null)}>✕</IconButton>
                </Tooltip>
              )}
            </Stack>
            {file && (
              <Box sx={{ mt: 1 }}>
                {file.type === "application/pdf" ? (
                  <iframe src={previewUrl || ""} title={`${key}-preview`} style={{ width: "100%", height: 160, border: "1px solid #e6eef8", borderRadius: 4 }} />
                ) : (
                  <Box component="img" src={previewUrl || ""} alt={key} sx={{ width: "100%", maxHeight: 200, objectFit: "contain", border: "1px solid #e6eef8", borderRadius: 1 }} />
                )}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{file.name} — {formatBytes(file.size)}</Typography>
              </Box>
            )}
            {!file && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No file chosen</Typography>
            )}
          </>
        )}
      </Paper>
    );
  };

  /* ---------- render ---------- */
  if (isVerifying) {
    return (
      <Box sx={{ backgroundColor: "#f6f7fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error && !reservation) {
    return (
      <Box sx={{ backgroundColor: "#f6f7fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const customerName = reservation?.customer?.name || "Guest";
  const customerEmail = reservation?.customer?.email || "";
  const storeName = storeDetails?.store_name || "";

  if (submittedSuccessfully) {
    return (
      <Box sx={{ backgroundColor: "#f6f7fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Paper elevation={6} sx={{ borderRadius: 4, overflow: "hidden", maxWidth: 500, width: "100%", textAlign: "center" }}>
          <Box sx={{ px: 5, py: 6 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: "50%",
              backgroundColor: "#e8f5e9", display: "flex", alignItems: "center",
              justifyContent: "center", mx: "auto", mb: 3,
            }}>
              <Typography sx={{ fontSize: 40, lineHeight: 1 }}>✅</Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: "#1a1a2e" }}>
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              Your documents have been successfully submitted. Our team will review them shortly.
            </Typography>
            <Box sx={{
              backgroundColor: "#f0f4ff", borderRadius: 2, px: 3, py: 2,
              border: "1px solid #d0deff",
            }}>
              <Typography variant="body2" sx={{ color: "#0047C0", fontWeight: 600 }}>
                You may now close this page.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f6f7fb", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 6 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Complete Documents — Upload & eSign
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload the required documents for each driver, then sign the waiver. Submit everything at once when ready.
        </Typography>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ backgroundColor: "#0047C0", color: "white", px: 2, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 600 }}>{storeName}</Typography>
            <Typography>{customerName}{customerEmail ? ` — ${customerEmail}` : ""}</Typography>
          </Box>
          {isSubmitting && <LinearProgress />}

          {/* Steps area */}
          <Box sx={{ p: 3 }}>
            {!storeDetails ? (
              <Box>
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={120} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </Box>
            ) : (
            <Grid item xs={12} md={12}>
              {activeSteps.length > 1 && (
                <Stepper activeStep={currentStepIndex} sx={{ mb: 3 }}>
                  {activeSteps.map((step) => (
                    <Step key={step.key}>
                      <StepLabel>
                        {step.key === "license" ? "Driver's License" : step.key === "insurance" ? "Insurance" : step.key === "bac" ? "BAC Test" : "Waiver & eSign"}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>

                {/* LICENSE STEP: one card per person who needs a license */}
                {currentStep === "license" && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Driver's License</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload a clear photo or scan of the driver's license for each required driver. Max 10 MB. Accepted: JPG, PNG, PDF.
                    </Typography>
                    {docRequests
                      .filter(r => r.docs.some(d => d.type === "DRIVER_LICENSE"))
                      .map(req => renderPersonDocCard(req, "DRIVER_LICENSE"))}
                  </Box>
                )}

                {/* INSURANCE STEP: one card per person who needs insurance */}
                {currentStep === "insurance" && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Insurance</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload proof of vehicle insurance for each required driver. Max 10 MB. Accepted: JPG, PNG, PDF.
                    </Typography>
                    {docRequests
                      .filter(r => r.docs.some(d => d.type === "INSURANCE"))
                      .map(req => renderPersonDocCard(req, "INSURANCE"))}
                  </Box>
                )}

                {/* BAC STEP */}
                {currentStep === "bac" && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Breathalyzer Test (BAC)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload a video of the breathalyzer test and enter the BAC score (0.00 – 0.10) for each required driver.
                    </Typography>
                    {docRequests
                      .filter(r => r.docs.some(d => d.type === "BAC_TEST"))
                      .map(req => renderBACCard(req))}
                  </Box>
                )}

                {/* AGREEMENT STEP */}
                {currentStep === "agreement" && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>Waiver & eSign</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Review the waiver, sign below, and click "Attach Signature".
                    </Typography>
                    {hasSignedAgreement ? (
                      <Alert severity="success" sx={{ mb: 2 }}>✅ Waiver already signed and submitted.</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 1 }}>
                            <iframe
                              id="waiverPdf"
                              title="waiver"
                              src={waiverPdfUrl}
                              style={{ width: "100%", height: 420, border: "1px solid #e6eef8", borderRadius: 6, transform: "translateZ(0)", isolation: "isolate" }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1, minHeight: 420 }}>
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                              <Box sx={{ position: "relative" }}>
                                <canvas
                                  id="sigPad"
                                  ref={(el) => (canvasRef.current = el)}
                                  style={{ width: "100%", height: 280, background: "#fff", border: "1px solid #e6eef8", borderRadius: 6, touchAction: "none", display: "block" }}
                                />
                                {!signaturePadDirty && (
                                  <Box sx={{
                                    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                                    alignItems: "center", justifyContent: "center", pointerEvents: "none",
                                  }}>
                                    <Typography variant="body2" sx={{ color: "#bbb", fontStyle: "italic", userSelect: "none" }}>Sign here</Typography>
                                    <Box sx={{ mt: 1, width: "60%", borderBottom: "1px dashed #ccc" }} />
                                  </Box>
                                )}
                              </Box>
                              {signatureDataUrl && (
                                <Alert severity="success" sx={{ py: 0 }}>Signature attached — ready to submit.</Alert>
                              )}
                              <Stack direction="row" spacing={1}>
                                <Button onClick={handleClearSignature}>Clear</Button>
                                <Button onClick={handleUndoSignature}>Undo</Button>
                                <Button onClick={handlePreview} variant="outlined">Preview</Button>
                              </Stack>
                              <Box sx={{ mt: 1 }}>
                                <Button variant="contained" onClick={handleAttachSignature} disabled={!signaturePadDirty && !signatureDataUrl}>Attach Signature</Button>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}
              </Paper>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}

              {/* Controls */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Button variant="outlined" onClick={handlePrev} disabled={isFirstStep}>← Previous</Button>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {!isLastStep && (
                    <Button variant="contained" onClick={handleNext}>Next →</Button>
                  )}
                  {isLastStep && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmitAll}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit All"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RentalAgreement;
